import { useState, useMemo, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import DialogContent from '@mui/material/DialogContent'
import OutlinedInput from '@mui/material/OutlinedInput'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'
import InputLabel from '@mui/material/InputLabel'
import Select, { type SelectChangeEvent } from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import { useGetWhiteListByBuildingIdQuery } from '@/api/whiteList'
import {
  useAddPlateToAbonementMutation,
  useGetAbonementsQuery,
  useUpdateAbonementPlateMutation
} from '@/api/abonements'
import { 
  getInitialForm, 
  getInitialAbonementForm,
  toApiAccessType,
  normalizePlateNumberInput,
  toIsoDate,
  getDefaultISOExpireDate,
  getGuestExpiresAt,
} from './helpers'
import { resolveUiAccessType } from '../helpers'
import { useAllowedAccessTypes } from '../useAllowedAccessTypes'
import { resolveAbonementId } from '@/features/abonements/helpers'
import { getAbonementApiErrorMessage } from '@/pages/abonements/abonement-utils'
import { ABONEMENT_STATUSES } from '@/pages/abonements/types'
import type {
  AccessUpsertForm,
  AbonementLinkForm,
  UiAccessType,
  UpsertModalParams,
  WhiteListRecord,
  WhiteListWritePayload
} from '../types'
import type { Building } from '@/types/building'
import AbonementPeriodInputs from './AbonementPeriodInputs'
import AccessPeriodInputs from './AccessPeriodInputs'
import GuestDurationInputs from './GuestDurationInputs'

const accessTypeOptions: { value: UiAccessType, labelKey: string }[] = [
  { value: 'white', labelKey: 'white_list' },
  { value: 'black', labelKey: 'black_list' },
  { value: 'guest', labelKey: 'guest_access' },
  { value: 'abonement', labelKey: 'abonement' },
]

type MutationTrigger = (payload: WhiteListWritePayload) => {
  unwrap: () => Promise<unknown>
}

type UpsertModalContentProps = {
  modalParams: UpsertModalParams | null
  whitelistItem?: WhiteListRecord
  activeBuildingId?: number
  activeBuilding?: Building
  updateList: MutationTrigger
  addNewNumber: MutationTrigger
  isLoading: boolean
  updateLoading: boolean
  onSaved: () => void
  onError: (message: string) => void
}

const UpsertModalContent = ({
  modalParams,
  whitelistItem,
  activeBuildingId,
  activeBuilding,
  updateList,
  addNewNumber,
  isLoading,
  updateLoading,
  onSaved,
  onError,
}: UpsertModalContentProps) => {
  const { t } = useTranslation()
  const { isAccessTypeAllowed } = useAllowedAccessTypes()
  const [updateAbonementPlate] = useUpdateAbonementPlateMutation()
  const [addPlateToAbonement] = useAddPlateToAbonementMutation()
  const { data: abonementsResponse } = useGetAbonementsQuery(
    { building_id: Number(activeBuildingId) },
    { skip: !activeBuildingId }
  )
  const [accessType, setAccessType] = useState<UiAccessType>(
    () => (modalParams?.accessType as UiAccessType) ?? resolveUiAccessType(whitelistItem) ?? 'white'
  )
  const [toDate, setToDate] = useState<string | Date | null>(() => (
    modalParams?.id ? null : getDefaultISOExpireDate(modalParams?.accessType)
  ))
  // Гостевой доступ на N минут от момента сохранения; null — до выбранной даты.
  const [guestDuration, setGuestDuration] = useState<number | null>(null)
  const [form, setForm] = useState<AccessUpsertForm>(() => getInitialForm(whitelistItem, activeBuildingId))
  const [abonementForm, setAbonementForm] = useState<AbonementLinkForm>(() => getInitialAbonementForm(whitelistItem))
  const normalizedPlateNumber = normalizePlateNumberInput(form.plateNumber)

  const { data: possibleDuplicates } = useGetWhiteListByBuildingIdQuery({ 
    buildingId: activeBuildingId, 
    page: 1, 
    size: 20, 
    plate_number: normalizedPlateNumber,
  }, {
    skip: !normalizedPlateNumber,
    selectFromResult: ({ data }) => ({
      data: Array.isArray(data?.data) ? data.data : []
    })
  })

  const duplicateEntry = useMemo(() => possibleDuplicates?.find((item) =>
    normalizePlateNumberInput(item?.plate_number) === normalizedPlateNumber 
    && Number(item?.id) !== Number(modalParams?.id)
  ), [
    possibleDuplicates,
    normalizedPlateNumber,
    modalParams?.id
  ])

  const handleChange = (event: { target: { name: string; value: unknown } }) => {
    const { name, value } = event.target

    if (name === 'plateNumber') {
      setForm((prev) => ({ ...prev, [name]: normalizePlateNumberInput(value) }))
      return
    }

    setForm((prev) => ({ 
      ...prev, 
      [name]: value as never
    }))
  }

  const handleAccessTypeChange = (value: UiAccessType) => {
    setForm((prev) => ({
      ...prev,
      inBlackList: value === 'black',
      is_guest: value === 'guest',
    }))
    setAbonementForm((prev) => ({
      abonement_id: value === 'abonement' ? prev.abonement_id : null,
      abonement_start: value === 'abonement' ? prev.abonement_start : null,
      abonement_end: value === 'abonement' ? prev.abonement_end : null,
    }))
    setToDate(getDefaultISOExpireDate(value))
    setGuestDuration(null)
    setAccessType(value)
  }

  const hasActiveBuilding = Number(activeBuildingId) > 0

  const handleSubmit = async (event?: FormEvent) => {
    event?.preventDefault?.()

    // Без объекта бэк сохраняет запись с building_id = 0 — камеры её не видят.
    if (!hasActiveBuilding) {
      onError(t('choose_building_first'))
      return
    }

    try {
      const abonementStartDate = toIsoDate(abonementForm.abonement_start)
      const abonementEndDate = toIsoDate(abonementForm.abonement_end)
      const expiresAt = accessType === 'abonement'
        ? abonementEndDate
        : accessType === 'guest' && guestDuration
        ? getGuestExpiresAt(guestDuration)
        : toDate
        ? toIsoDate(toDate)?.replace(/T\d{2}:\d{2}:\d{2}/, 'T23:59:59')
        : toIsoDate(form.expires || whitelistItem?.expires_at)

      if (accessType === 'abonement') {
        if (!abonementForm.abonement_id) {
          onError(t('choose_abonement'))
          return
        }
        if (!abonementStartDate) {
          onError(t('start_date_required'))
          return
        }
        if (!abonementEndDate) {
          onError(t('expire_date_required'))
          return
        }
        if (new Date(abonementEndDate) <= new Date(abonementStartDate)) {
          onError(t('expire_must_be_after_start'))
          return
        }
      }

      const unwrapWithFallback = async (
        request: MutationTrigger,
        payload: WhiteListWritePayload
      ) => {
        try {
          return await request(payload).unwrap()
        } catch (error) {
          const errorText = getAbonementApiErrorMessage(error, t).toLowerCase()
          const shouldRetryWithoutPeriod = errorText.includes('start_date') 
            || errorText.includes('end_date')
          if (!shouldRetryWithoutPeriod) throw error
          const fallbackPayload = { ...payload }
          delete fallbackPayload.start_date
          delete fallbackPayload.end_date
          return request(fallbackPayload).unwrap()
        }
      }

      // Повторное добавление того же ГРНЗ → обновляем существующую запись WL.
      const existingRecord = modalParams?.id
        ? whitelistItem
        : duplicateEntry || undefined
      const existingRecordId = existingRecord?.id
        ? Number(existingRecord.id)
        : Number(modalParams?.id) || null

      let linkedAbonementId =
        accessType === 'abonement' ? resolveAbonementId(existingRecord) : null

      // Абонемент: долг в Kaspi (NOT_PAID). Доступ в WL — только после оплаты.
      if (accessType === 'abonement' && abonementStartDate && abonementEndDate) {
        if (!linkedAbonementId) {
          const selected = abonementsResponse?.list.find(
            (ab) => Number(ab.id) === Number(abonementForm.abonement_id)
          )
          if (!selected?.payment_code) {
            onError(t('choose_abonement'))
            return
          }
          const addPlateResult = await addPlateToAbonement({
            payment_code: selected.payment_code,
            plate_number: String(form.plateNumber || '').trim().toUpperCase(),
          }).unwrap()
          const created = Array.isArray(addPlateResult?.data)
            ? addPlateResult.data[0]
            : addPlateResult?.data
          linkedAbonementId = created?.id ? Number(created.id) : null
        }

        if (!linkedAbonementId) {
          onError(t('error_occurred'))
          return
        }

        // Через admin-service: период фиксируется и оплата его не сдвигает.
        await updateAbonementPlate({
          id: Number(linkedAbonementId),
          start_at: abonementStartDate,
          expire_at: abonementEndDate,
          status: ABONEMENT_STATUSES.NOT_PAID,
          paid_amount: 0,
        }).unwrap()
      }

      const payload: WhiteListWritePayload = {
        phone_number: form.phone_number,
        comment: form.comment,
        is_guest: accessType === 'guest',
        access_type: toApiAccessType(accessType),
        plate_number: String(form.plateNumber || '').trim().toUpperCase(),
        in_black_list: accessType === 'black',
        channel_id: form.channel,
        // До оплаты доступа нет; после Kaspi SyncWhiteList выставит expires_at.
        expires_at:
          accessType === 'abonement' ? new Date().toISOString() : expiresAt,
        ...(+Number(activeBuildingId) === 237 ? { 
          tariff_code: form.tariff_code || null 
        } : {}),
        ...(existingRecordId ? { 
          id: existingRecordId,
          building_id: existingRecord?.building_id || Number(activeBuildingId),
        } : {
          building_id: Number(activeBuildingId),
        }),
        ...(accessType === 'abonement' && linkedAbonementId ? {
          start_date: abonementStartDate,
          end_date: abonementEndDate,
          abonement_id: Number(linkedAbonementId),
          subscription_id: Number(linkedAbonementId),
        } : {
          abonement_id: null,
          subscription_id: null,
        }),
      }

      if (existingRecordId) {
        await unwrapWithFallback(updateList, payload)
      } else {
        await unwrapWithFallback(addNewNumber, payload)
      }
      onSaved()
    } catch (error) {
      onError(getAbonementApiErrorMessage(error, t) || t('error_occurred'))
    }
  }

  return (
    <DialogContent dividers>
      <Box
        component="form"
        autoComplete="off"
        noValidate
        onSubmit={handleSubmit}
      >
      <TextField
        sx={{ mb: 2 }}
        label={t('building')}
        value={activeBuilding?.name || ''}
        fullWidth
        disabled
      />
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>{t('choose_access_type')}</InputLabel>
        <Select
          name="access_type"
          value={accessType}
          onChange={(e: SelectChangeEvent) =>
            handleAccessTypeChange(e.target.value as UiAccessType)
          }
          label={t('choose_access_type')}
        >
          {accessTypeOptions
            .filter(({ value }) => value === accessType || isAccessTypeAllowed(value))
            .map(({ value, labelKey }) => (
              <MenuItem key={value} value={value}>{t(labelKey)}</MenuItem>
            ))}
        </Select>
      </FormControl>
      <Divider />
      <FormControl fullWidth sx={{ mb: 2, mt: 2 }}>
        <InputLabel>
          {t('enter_the_car_number_in_Latin_characters')}
        </InputLabel>
        <OutlinedInput
          name="plateNumber"
          value={form.plateNumber}
          onChange={handleChange}
          label={t('enter_the_car_number_in_Latin_characters')}
        />
      </FormControl>
      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>{t('contact_phone_number')}</InputLabel>
        <OutlinedInput
          name="phone_number"
          value={form.phone_number}
          onChange={handleChange}
          label={t('contact_phone_number')}
          autoComplete="new-password"
          inputProps={{
            autoComplete: 'new-password',
            'data-form-type': 'other'
          }}
        />
      </FormControl>
      <TextField
        sx={{ mb: 3 }}
        label={t('comment')}
        name="comment"
        value={form.comment}
        minRows={1}
        fullWidth
        onChange={handleChange}
      />
      {
        Number(activeBuildingId) === 237 && (
          <TariffGroupSelector 
            value={form.tariff_code || ''}
            onChange={handleChange}
          />
        )
      }
      {
        accessType === 'abonement' ? (
          <AbonementPeriodInputs 
            abonementId={abonementForm.abonement_id}
            abonementStartDate={abonementForm.abonement_start}
            abonementEndDate={abonementForm.abonement_end}
            setAbonementForm={(newValue) => setAbonementForm(newValue)}
          />
        ) : accessType === 'guest' ? (
          <GuestDurationInputs
            duration={guestDuration}
            onDurationChange={setGuestDuration}
            date={toDate || form.expires || null}
            onDateChange={(newValue) => setToDate(newValue)}
          />
        ) : (
          <AccessPeriodInputs
            value={toDate || form.expires || null}
            onChange={(newValue) => setToDate(newValue)}
          />
        )
      }
      <Button
        sx={{ mt: 3 }}
        fullWidth
        type="submit"
        variant="contained"
        disabled={isLoading || updateLoading || !hasActiveBuilding}
      >
        {modalParams?.id ? t('save') : t('add')}
      </Button>
      </Box>
    </DialogContent>
  )
}

const TariffGroupSelector = ({
  value,
  onChange
}: {
  value: string
  onChange: (event: SelectChangeEvent) => void
}) => {
  return (
    <FormControl fullWidth sx={{ mb: 3 }}>
      <InputLabel>Тарифная группа</InputLabel>
      <Select
        name="tariff_code"
        value={value}
        onChange={onChange}
        label="Тарифная группа"
      >
        <MenuItem value="LeoneVIP">VIP - 200 тг/час</MenuItem>
        <MenuItem value="LeoneStd">Стандарт - 500 тг/час</MenuItem>
      </Select>
      <FormHelperText>
        Если ничего не выбрать - будет применён обычный тариф 1000
        тг/час
      </FormHelperText>
    </FormControl>
  )
}

export default UpsertModalContent